import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import {
  listEmailSenders,
  getEmailRoutes,
  listSesIdentities,
  updateEmailRoutes,
  createEmailSender,
  updateEmailSender,
  deleteEmailSender,
  sendEmailSenderTest,
  type EmailSender,
  type EmailRoutes,
  type EmailSenderCreate,
  type EmailSenderUpdate,
  type EmailTransport,
  type SesIdentity,
} from '../../lib/api/admin';
import { fmtDateTime } from '../../lib/format';

/**
 * Senders tab — configurable email sending domain / transport.
 *
 *   1. Routing card (top): which sender profile sends OTP vs episode reminders.
 *      The OTP row is the failover lever — flip it to the backup domain if the
 *      primary's reputation drops. Takes effect within seconds (the API busts
 *      its sender cache on save).
 *   2. Sender profiles: SES (pick a verified identity) or SMTP (host/port/user/
 *      password, stored encrypted). Each has a test-send so a domain can be
 *      proven before live OTP is routed to it.
 *
 * If a route points at a disabled/missing profile the API silently falls back to
 * the SES_FROM_* env defaults, so OTP never hard-fails on a misconfiguration.
 */

type Message = { kind: 'error' | 'success'; text: string };
type Labeled = { type: string; label: string };

const TYPE_LABELS: Record<string, string> = {
  otp: 'OTP login codes',
  'episode-reminder': 'Episode reminders',
};

function typeLabel(t: string): string {
  return TYPE_LABELS[t] ?? t;
}

function errText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function identityFor(fromEmail: string, identities: SesIdentity[]): SesIdentity | undefined {
  const addr = fromEmail.trim().toLowerCase();
  const domain = addr.split('@')[1] ?? '';
  return identities.find((i) => i.identity.toLowerCase() === addr || i.identity.toLowerCase() === domain);
}

export function EmailSendersTab() {
  const [senders, setSenders] = useState<EmailSender[] | null>(null);
  const [routes, setRoutes] = useState<EmailRoutes | null>(null);
  const [identities, setIdentities] = useState<SesIdentity[] | null>(null);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [region, setRegion] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [topError, setTopError] = useState<string | null>(null);

  const reloadSenders = async (): Promise<void> => {
    setSenders(await listEmailSenders());
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    Promise.all([
      listEmailSenders({ signal: controller.signal }),
      getEmailRoutes({ signal: controller.signal }),
    ])
      .then(([s, r]) => {
        setSenders(s);
        setRoutes(r);
        setTopError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setTopError(errText(err, "Couldn't load email senders."));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    // SES identities are a live AWS read — load separately so a perms/throttle
    // failure degrades the picker to a free-text field instead of blocking the tab.
    listSesIdentities({ signal: controller.signal })
      .then((d) => {
        setIdentities(d.identities);
        setRegion(d.region);
        setIdentityError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setIdentityError(errText(err, 'SES identity list unavailable.'));
      });

    return () => controller.abort();
  }, []);

  if (loading && !senders) {
    return <p className="text-sm text-white/55">Loading senders…</p>;
  }
  if (topError && !senders) {
    return <p className="text-sm text-[#ff8da6]">{topError}</p>;
  }

  return (
    <div className="flex flex-col gap-8">
      <RoutingCard
        senders={senders ?? []}
        routes={routes}
        onRoutesChange={setRoutes}
      />
      <SendersList
        senders={senders ?? []}
        routes={routes}
        identities={identities}
        identityError={identityError}
        region={region}
        onChanged={reloadSenders}
      />
    </div>
  );
}

// ─── Routing card ────────────────────────────────────────────────────

function RoutingCard({
  senders,
  routes,
  onRoutesChange,
}: {
  senders: EmailSender[];
  routes: EmailRoutes | null;
  onRoutesChange: (r: EmailRoutes) => void;
}) {
  const [draft, setDraft] = useState<Record<string, string>>(routes?.routes ?? {});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  useEffect(() => {
    if (routes) setDraft(routes.routes);
  }, [routes]);

  const types: Labeled[] = (routes?.types ?? []).map((t) => ({ type: t, label: typeLabel(t) }));
  const dirty = useMemo(() => {
    if (!routes) return false;
    return types.some((t) => draft[t.type] !== routes.routes[t.type]);
  }, [draft, routes, types]);

  const senderByKey = useMemo(() => new Map(senders.map((s) => [s.senderKey, s])), [senders]);

  const save = async (): Promise<void> => {
    if (!routes) return;
    const changed: Record<string, string> = {};
    for (const t of types) {
      if (draft[t.type] && draft[t.type] !== routes.routes[t.type]) changed[t.type] = draft[t.type]!;
    }
    if (Object.keys(changed).length === 0) return;

    setSaving(true);
    setMessage(null);
    try {
      const next = await updateEmailRoutes(changed);
      onRoutesChange({ routes: next.routes, types: next.types });
      setDraft(next.routes);
      setMessage({
        kind: 'success',
        text: next.warnings.length ? `Saved. ${next.warnings.join(' ')}` : 'Routing saved.',
      });
    } catch (err) {
      setMessage({ kind: 'error', text: errText(err, "Couldn't save routing.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-brand-redAccent/40 bg-black/20 p-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-xl uppercase tracking-wide text-white">Sending routes</h2>
          <p className="text-sm text-white/55">
            Which sender profile each email type uses. <strong className="text-white/80">OTP is the failover
            lever</strong> — switch it to a backup domain if the primary&rsquo;s reputation drops. Saves apply
            within seconds.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || saving}
          className="rounded-full bg-[#00ff8b] px-4 py-2 text-xs font-medium uppercase tracking-wide text-[#10231a] transition hover:bg-[#5bffb4] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save routing'}
        </button>
      </header>

      {message && (
        <p className={`mt-3 text-sm ${message.kind === 'error' ? 'text-[#ff8da6]' : 'text-[#5bffb4]'}`}>
          {message.text}
        </p>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {types.map((t) => {
          const selectedKey = draft[t.type] ?? '';
          const selected = senderByKey.get(selectedKey);
          return (
            <div key={t.type} className="rounded-lg border border-white/10 bg-black/30 p-4">
              <Field label={t.label}>
                <select
                  value={selectedKey}
                  onChange={(e) => setDraft((cur) => ({ ...cur, [t.type]: e.target.value }))}
                  className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
                >
                  {senders.map((s) => (
                    <option key={s.senderKey} value={s.senderKey} className="bg-black">
                      {s.label}
                      {s.enabled ? '' : ' (disabled)'}
                    </option>
                  ))}
                </select>
              </Field>
              <p className="mt-2 text-xs text-white/55">
                {selected ? (
                  <>
                    From <span className="text-white/80">{selected.fromEmail}</span> · {selected.transport.toUpperCase()}
                    {selected.enabled ? '' : ' · ⚠ disabled (falls back to env default)'}
                  </>
                ) : (
                  'No sender selected.'
                )}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── Sender profiles ─────────────────────────────────────────────────

function SendersList({
  senders,
  routes,
  identities,
  identityError,
  region,
  onChanged,
}: {
  senders: EmailSender[];
  routes: EmailRoutes | null;
  identities: SesIdentity[] | null;
  identityError: string | null;
  region: string;
  onChanged: () => Promise<void>;
}) {
  const [creating, setCreating] = useState(false);

  const usedKeys = useMemo(
    () => new Set(Object.values(routes?.routes ?? {})),
    [routes],
  );

  return (
    <section className="rounded-xl border border-white/15 bg-black/20 p-5">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-xl uppercase tracking-wide text-white">Sender profiles</h2>
          <p className="text-sm text-white/55">
            SES (a verified identity in {region || 'the configured region'}) or SMTP (host/port/user; password
            stored encrypted). A profile in use by a route can&rsquo;t be deleted — reassign the route first.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="rounded-full border border-white/25 px-4 py-2 text-xs uppercase tracking-wide text-white/80 transition hover:bg-white/10"
        >
          {creating ? 'Close' : 'Add sender'}
        </button>
      </header>

      {identityError && (
        <p className="mt-3 text-xs text-[#ffae5b]">
          SES identity list unavailable ({identityError}). You can still type a From address; it just won&rsquo;t
          be checked against verified identities.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-4">
        {creating && (
          <CreateSenderForm
            identities={identities}
            onCreated={async () => {
              setCreating(false);
              await onChanged();
            }}
          />
        )}
        {senders.map((s) => (
          <SenderEditor
            key={s.senderKey}
            sender={s}
            identities={identities}
            inUse={usedKeys.has(s.senderKey)}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
}

interface SenderDraft {
  label: string;
  transport: EmailTransport;
  fromEmail: string;
  fromName: string;
  replyToEmail: string;
  supportEmail: string;
  region: string;
  enabled: boolean;
  smtpHost: string;
  smtpPort: string;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword: string;
}

function senderToDraft(s: EmailSender): SenderDraft {
  return {
    label: s.label,
    transport: s.transport,
    fromEmail: s.fromEmail,
    fromName: s.fromName,
    replyToEmail: s.replyToEmail ?? '',
    supportEmail: s.supportEmail,
    region: s.region ?? '',
    enabled: s.enabled,
    smtpHost: s.smtpHost ?? '',
    smtpPort: s.smtpPort != null ? String(s.smtpPort) : '',
    smtpSecure: s.smtpSecure ?? false,
    smtpUser: s.smtpUser ?? '',
    smtpPassword: '',
  };
}

function SenderEditor({
  sender,
  identities,
  inUse,
  onChanged,
}: {
  sender: EmailSender;
  identities: SesIdentity[] | null;
  inUse: boolean;
  onChanged: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<SenderDraft>(() => senderToDraft(sender));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    setDraft(senderToDraft(sender));
  }, [sender]);

  const patch = (p: Partial<SenderDraft>): void => {
    setDraft((cur) => ({ ...cur, ...p }));
    setMessage(null);
  };

  const dirty = useMemo(() => {
    const base = senderToDraft(sender);
    return (Object.keys(base) as Array<keyof SenderDraft>).some((k) =>
      k === 'smtpPassword' ? draft.smtpPassword !== '' : draft[k] !== base[k],
    );
  }, [draft, sender]);

  const buildUpdate = (): EmailSenderUpdate | null => {
    const base = senderToDraft(sender);
    const out: EmailSenderUpdate = {};
    if (draft.label !== base.label) out.label = draft.label.trim();
    if (draft.transport !== base.transport) out.transport = draft.transport;
    if (draft.fromEmail !== base.fromEmail) out.fromEmail = draft.fromEmail.trim();
    if (draft.fromName !== base.fromName) out.fromName = draft.fromName.trim();
    if (draft.replyToEmail !== base.replyToEmail) out.replyToEmail = draft.replyToEmail.trim() || null;
    if (draft.supportEmail !== base.supportEmail) out.supportEmail = draft.supportEmail.trim();
    if (draft.region !== base.region) out.region = draft.region.trim() || null;
    if (draft.enabled !== base.enabled) out.enabled = draft.enabled;
    if (draft.transport === 'smtp') {
      if (draft.smtpHost !== base.smtpHost) out.smtpHost = draft.smtpHost.trim() || null;
      if (draft.smtpPort !== base.smtpPort) out.smtpPort = draft.smtpPort ? Number(draft.smtpPort) : null;
      if (draft.smtpSecure !== base.smtpSecure) out.smtpSecure = draft.smtpSecure;
      if (draft.smtpUser !== base.smtpUser) out.smtpUser = draft.smtpUser.trim() || null;
      if (draft.smtpPassword !== '') out.smtpPassword = draft.smtpPassword;
    }
    return Object.keys(out).length ? out : null;
  };

  const save = async (): Promise<void> => {
    const update = buildUpdate();
    if (!update) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await updateEmailSender(sender.senderKey, update);
      setMessage({
        kind: 'success',
        text: res.warnings.length ? `Saved. ${res.warnings.join(' ')}` : 'Saved.',
      });
      await onChanged();
    } catch (err) {
      setMessage({ kind: 'error', text: errText(err, "Couldn't save sender.") });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (!window.confirm(`Delete sender "${sender.label}"? This can't be undone.`)) return;
    setBusy(true);
    setMessage(null);
    try {
      await deleteEmailSender(sender.senderKey);
      await onChanged();
    } catch (err) {
      setMessage({ kind: 'error', text: errText(err, "Couldn't delete sender.") });
      setBusy(false);
    }
  };

  const sendTest = async (): Promise<void> => {
    if (!testTo.trim()) {
      setMessage({ kind: 'error', text: 'Enter a recipient address for the test.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const { messageId } = await sendEmailSenderTest(sender.senderKey, testTo.trim());
      setMessage({ kind: 'success', text: `Test sent${messageId ? ` (id ${messageId})` : ''}.` });
    } catch (err) {
      setMessage({ kind: 'error', text: errText(err, 'Test send failed.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-heading text-sm uppercase tracking-wide text-white">
          {sender.label}{' '}
          <span className="text-white/40">({sender.senderKey})</span>
        </h3>
        <div className="flex flex-wrap items-center gap-3 text-xs text-white/55">
          {inUse && <span className="rounded bg-[#0f2a3d] px-2 py-0.5 text-[#5bd6ff]">In use</span>}
          <span
            className={`rounded px-2 py-0.5 ${
              sender.enabled ? 'bg-[#0f3d2a] text-[#5bffb4]' : 'bg-[#3d240f] text-[#ffae5b]'
            }`}
          >
            {sender.enabled ? 'Enabled' : 'Disabled'}
          </span>
          {sender.modifiedAt && <span>Last edit: {fmtDateTime(sender.modifiedAt)}</span>}
        </div>
      </div>

      <SenderFields draft={draft} patch={patch} identities={identities} />

      {message && (
        <p className={`mt-3 text-sm ${message.kind === 'error' ? 'text-[#ff8da6]' : 'text-[#5bffb4]'}`}>
          {message.text}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || busy}
          className="rounded-full bg-[#00ff8b] px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-[#10231a] transition hover:bg-[#5bffb4] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Working…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setDraft(senderToDraft(sender))}
          disabled={!dirty || busy}
          className="rounded-full border border-white/25 px-4 py-1.5 text-xs uppercase tracking-wide text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Revert
        </button>
        <button
          type="button"
          onClick={() => void remove()}
          disabled={busy || inUse}
          title={inUse ? 'Reassign the route using this sender before deleting.' : undefined}
          className="rounded-full border border-[#ff8da6]/40 px-4 py-1.5 text-xs uppercase tracking-wide text-[#ff8da6] transition hover:bg-[#ff8da6]/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Delete
        </button>

        <div className="ml-auto flex items-center gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="test@example.com"
            className="w-48 rounded-md border border-white/15 bg-black/30 px-2 py-1.5 text-sm text-white focus:border-white/40 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void sendTest()}
            disabled={busy}
            className="rounded-full border border-white/25 px-4 py-1.5 text-xs uppercase tracking-wide text-white/80 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Send test
          </button>
        </div>
      </div>
    </div>
  );
}

function SenderFields({
  draft,
  patch,
  identities,
}: {
  draft: SenderDraft;
  patch: (p: Partial<SenderDraft>) => void;
  identities: SesIdentity[] | null;
}) {
  const matched = identities ? identityFor(draft.fromEmail, identities) : undefined;

  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      <Field label="Label">
        <input
          type="text"
          value={draft.label}
          onChange={(e) => patch({ label: e.target.value })}
          maxLength={128}
          className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
        />
      </Field>
      <Field label="Transport">
        <select
          value={draft.transport}
          onChange={(e) => patch({ transport: e.target.value as EmailTransport })}
          className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
        >
          <option value="ses" className="bg-black">AWS SES</option>
          <option value="smtp" className="bg-black">SMTP</option>
        </select>
      </Field>

      <Field label="From email">
        <input
          type="email"
          value={draft.fromEmail}
          onChange={(e) => patch({ fromEmail: e.target.value })}
          maxLength={320}
          className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
        />
      </Field>
      <Field label="From name">
        <input
          type="text"
          value={draft.fromName}
          onChange={(e) => patch({ fromName: e.target.value })}
          maxLength={128}
          className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
        />
      </Field>

      <Field label="Reply-To (optional)">
        <input
          type="email"
          value={draft.replyToEmail}
          onChange={(e) => patch({ replyToEmail: e.target.value })}
          maxLength={320}
          placeholder="(none)"
          className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
        />
      </Field>
      <Field label="In-body support@ address">
        <input
          type="email"
          value={draft.supportEmail}
          onChange={(e) => patch({ supportEmail: e.target.value })}
          maxLength={320}
          className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
        />
      </Field>

      {draft.transport === 'ses' ? (
        <>
          <Field label="Region (optional — default if blank)">
            <input
              type="text"
              value={draft.region}
              onChange={(e) => patch({ region: e.target.value })}
              maxLength={64}
              placeholder="(default)"
              className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
            />
          </Field>
          <div className="flex flex-col justify-end">
            {identities && identities.length > 0 && (
              <Field label="Verified identities (reference)">
                <select
                  value=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) return;
                    // Domain identity → suggest no-reply@domain; email identity → use as-is.
                    patch({ fromEmail: id.includes('@') ? id : `no-reply@${id}` });
                  }}
                  className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
                >
                  <option value="" className="bg-black">Pick a verified identity…</option>
                  {identities.map((i) => (
                    <option key={i.identity} value={i.identity} className="bg-black">
                      {i.identity}
                      {i.sendingEnabled === false ? ' (sending off)' : ''}
                      {i.dkim && i.dkim !== 'SUCCESS' ? ` (DKIM ${i.dkim})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <SesStatusNote fromEmail={draft.fromEmail} matched={matched} hasIdentities={!!identities} />
          </div>
        </>
      ) : (
        <>
          <Field label="SMTP host">
            <input
              type="text"
              value={draft.smtpHost}
              onChange={(e) => patch({ smtpHost: e.target.value })}
              maxLength={255}
              className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
            />
          </Field>
          <Field label="SMTP port">
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={65535}
              value={draft.smtpPort}
              onChange={(e) => patch({ smtpPort: e.target.value })}
              className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
            />
          </Field>
          <Field label="SMTP user">
            <input
              type="text"
              value={draft.smtpUser}
              onChange={(e) => patch({ smtpUser: e.target.value })}
              maxLength={320}
              className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
            />
          </Field>
          <Field label="SMTP password">
            <input
              type="password"
              value={draft.smtpPassword}
              onChange={(e) => patch({ smtpPassword: e.target.value })}
              maxLength={1024}
              autoComplete="new-password"
              placeholder="•••• (unchanged)"
              className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-white/85">
            <input
              type="checkbox"
              checked={draft.smtpSecure}
              onChange={(e) => patch({ smtpSecure: e.target.checked })}
              className="h-4 w-4 rounded border-white/25 bg-black/30"
            />
            Implicit TLS (port 465). Leave off for STARTTLS (587).
          </label>
        </>
      )}

      <label className="flex items-center gap-2 text-sm text-white/85 sm:col-span-2">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => patch({ enabled: e.target.checked })}
          className="h-4 w-4 rounded border-white/25 bg-black/30"
        />
        Enabled (a disabled profile is skipped — routes pointing at it fall back to the env default)
      </label>
    </div>
  );
}

function SesStatusNote({
  fromEmail,
  matched,
  hasIdentities,
}: {
  fromEmail: string;
  matched: SesIdentity | undefined;
  hasIdentities: boolean;
}) {
  if (!hasIdentities || !fromEmail.trim()) return null;
  if (!matched) {
    return <p className="mt-2 text-xs text-[#ff8da6]">⚠ No verified SES identity matches this From address.</p>;
  }
  const warn = matched.sendingEnabled === false || (matched.dkim && matched.dkim !== 'SUCCESS');
  return (
    <p className={`mt-2 text-xs ${warn ? 'text-[#ffae5b]' : 'text-[#5bffb4]'}`}>
      {matched.identity}: {matched.verificationStatus ?? 'status?'}
      {matched.sendingEnabled === false ? ' · sending OFF' : ''}
      {matched.dkim ? ` · DKIM ${matched.dkim}` : ''}
    </p>
  );
}

// ─── Create form ─────────────────────────────────────────────────────

const EMPTY_CREATE: SenderDraft & { senderKey: string } = {
  senderKey: '',
  label: '',
  transport: 'ses',
  fromEmail: '',
  fromName: 'State Farm Gamerhood',
  replyToEmail: '',
  supportEmail: 'support@statefarmgamerhood.com',
  region: '',
  enabled: true,
  smtpHost: '',
  smtpPort: '587',
  smtpSecure: false,
  smtpUser: '',
  smtpPassword: '',
};

function CreateSenderForm({
  identities,
  onCreated,
}: {
  identities: SesIdentity[] | null;
  onCreated: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<SenderDraft & { senderKey: string }>(EMPTY_CREATE);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  const patch = (p: Partial<SenderDraft & { senderKey: string }>): void => {
    setDraft((cur) => ({ ...cur, ...p }));
    setMessage(null);
  };

  const submit = async (): Promise<void> => {
    if (!/^[a-z0-9-]+$/.test(draft.senderKey)) {
      setMessage({ kind: 'error', text: 'Key must be lowercase letters, numbers, and dashes.' });
      return;
    }
    if (!draft.label.trim() || !draft.fromEmail.trim() || !draft.supportEmail.trim()) {
      setMessage({ kind: 'error', text: 'Label, From email, and support email are required.' });
      return;
    }
    const body: EmailSenderCreate = {
      senderKey: draft.senderKey,
      label: draft.label.trim(),
      transport: draft.transport,
      fromEmail: draft.fromEmail.trim(),
      fromName: draft.fromName.trim(),
      replyToEmail: draft.replyToEmail.trim() || null,
      supportEmail: draft.supportEmail.trim(),
      region: draft.region.trim() || null,
      enabled: draft.enabled,
    };
    if (draft.transport === 'smtp') {
      body.smtpHost = draft.smtpHost.trim();
      body.smtpPort = draft.smtpPort ? Number(draft.smtpPort) : undefined;
      body.smtpSecure = draft.smtpSecure;
      body.smtpUser = draft.smtpUser.trim();
      body.smtpPassword = draft.smtpPassword;
    }

    setBusy(true);
    setMessage(null);
    try {
      const res = await createEmailSender(body);
      if (res.warnings.length) {
        setMessage({ kind: 'success', text: `Created. ${res.warnings.join(' ')}` });
      }
      await onCreated();
    } catch (err) {
      setMessage({ kind: 'error', text: errText(err, "Couldn't create sender.") });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#5bffb4]/30 bg-black/30 p-4">
      <h3 className="font-heading text-sm uppercase tracking-wide text-white">New sender</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Field label="Key (immutable)">
          <input
            type="text"
            value={draft.senderKey}
            onChange={(e) => patch({ senderKey: e.target.value })}
            maxLength={64}
            placeholder="e.g. activations-smtp"
            className="w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
          />
        </Field>
        <div />
      </div>

      <SenderFields draft={draft} patch={patch} identities={identities} />

      {message && (
        <p className={`mt-3 text-sm ${message.kind === 'error' ? 'text-[#ff8da6]' : 'text-[#5bffb4]'}`}>
          {message.text}
        </p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="rounded-full bg-[#00ff8b] px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-[#10231a] transition hover:bg-[#5bffb4] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create sender'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
      {label}
      {children}
    </label>
  );
}
